import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import UserLayout from '@/components/Layout/UserLayout';
import { Badge, Button, Card, EmptyState, FormSearchInput, FormSelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { normalizeGlobalExitCandidateSearchQuery } from '@/components/UI/exitCandidateSelectItems';

type CandidateSourceFilter = '' | 'base_strategy' | 'manual' | 'aero' | 'system_grid' | 'imported';

const SOURCE_OPTIONS: { value: CandidateSourceFilter; label: string }[] = [
    { value: '', label: 'All sources' },
    { value: 'aero', label: 'AERO' },
    { value: 'base_strategy', label: 'Base strategy' },
    { value: 'manual', label: 'Manual' },
    { value: 'system_grid', label: 'System grid' },
    { value: 'imported', label: 'Imported' }
];

const SOURCE_LABELS: Record<Exclude<CandidateSourceFilter, ''>, string> = {
    aero: 'AERO',
    base_strategy: 'Base strategy',
    imported: 'Imported',
    manual: 'Manual',
    system_grid: 'System grid'
};

const USAGE_LABELS: Record<string, string> = {
    aeroPromotionCount: 'AERO promotions',
    experimentObjectiveCount: 'Experiment objectives',
    liveTradeCount: 'Live trades',
    portfolioTradeCount: 'Portfolio trades',
    strategyAttachmentCount: 'Strategy attachments',
    strategyBacktestCount: 'Strategy backtests'
};

const ExitCandidateDetail = ({ candidateId }: { candidateId: number }) => {
    const detailsQuery = useQuery({
        ...trpc.tradeLogicExitCandidates.getGlobalCandidateDetails.queryOptions({ candidateId }),
        staleTime: 30000
    });

    if (detailsQuery.isLoading) {
        return <DataLoadingSpinner text="Loading global exit candidate…" />;
    }

    if (detailsQuery.isError) {
        return (
            <EmptyState
                action={
                    <Button size="sm" variant="outline-dark" onClick={() => detailsQuery.refetch()}>
                        Retry
                    </Button>
                }
                description={detailsQuery.error.message}
                title="Could not load this exit candidate"
            />
        );
    }

    const details = detailsQuery.data;
    const candidate = details?.candidate;
    if (!details || !candidate) return <EmptyState title={`Global exit candidate #${candidateId} was not found`} />;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl font-semibold text-zinc-100">{candidate.label}</h1>
                        <Badge variant={candidate.status === 'active' ? 'success' : 'danger'}>{candidate.status}</Badge>
                        <Badge variant="dark">#{candidate.id}</Badge>
                    </div>
                    {candidate.description && <p className="mt-2 text-sm text-zinc-400">{candidate.description}</p>}
                </div>
                <Button size="sm" to="/user/exitCandidates" variant="outline-dark">
                    All candidates
                </Button>
            </div>

            <Card title="Identity">
                <dl className="grid gap-3 text-sm md:grid-cols-2">
                    <Detail label="Source" value={SOURCE_LABELS[candidate.source]} />
                    <Detail label="Exit version" value={`#${candidate.tradeLogicV2ExitVersionId}`} />
                    <Detail label="Candidate hash" value={candidate.candidateHash} mono />
                    <Detail label="Exit fingerprint" value={candidate.exitFingerprint ?? 'None'} mono />
                    <Detail label="Candidate key" value={candidate.candidateKey ?? 'None'} mono />
                    <Detail label="Registry fingerprint" value={candidate.registryManifestFingerprint} mono />
                </dl>
            </Card>

            <Card title="Usage">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {Object.entries(details.usage).map(([key, value]) => (
                        <div className="rounded-md border border-zinc-800 bg-black/20 p-3" key={key}>
                            <div className="text-xs uppercase tracking-wide text-zinc-500">{USAGE_LABELS[key] ?? key}</div>
                            <div className="mt-1 text-xl font-semibold text-zinc-100">{value.toLocaleString()}</div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card title="Immutable artifact">
                <div className="grid gap-3 lg:grid-cols-2">
                    <JsonBlock label="Parameters" value={candidate.parameterValues} />
                    <JsonBlock label="Provenance" value={candidate.provenance} />
                </div>
            </Card>
        </div>
    );
};

const Detail = ({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) => (
    <div>
        <dt className="text-xs uppercase tracking-wide text-zinc-500">{label}</dt>
        <dd className={`mt-1 break-all text-zinc-200 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
);

const JsonBlock = ({ label, value }: { label: string; value: unknown }) => (
    <div>
        <div className="mb-2 text-sm font-medium text-zinc-200">{label}</div>
        <pre className="max-h-96 overflow-auto rounded-md border border-zinc-800 bg-black/30 p-3 text-xs text-zinc-300">
            {JSON.stringify(value, null, 2)}
        </pre>
    </div>
);

const ExitCandidateList = () => {
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [source, setSource] = useState<CandidateSourceFilter>('');
    const [status, setStatus] = useState<'active' | 'invalid'>('active');
    const [cursor, setCursor] = useState<number | null>(null);
    const [cursorHistory, setCursorHistory] = useState<(number | null)[]>([]);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedSearchText(normalizeGlobalExitCandidateSearchQuery(searchText)), 250);
        return () => window.clearTimeout(timeout);
    }, [searchText]);

    useEffect(() => {
        setCursor(null);
        setCursorHistory([]);
    }, [debouncedSearchText, source, status]);

    const candidatesQuery = useQuery({
        ...trpc.tradeLogicExitCandidates.searchGlobalCandidates.queryOptions({
            ...(debouncedSearchText ? { query: debouncedSearchText } : {}),
            ...(source ? { source } : {}),
            ...(cursor !== null ? { cursor } : {}),
            status,
            limit: 50
        }),
        staleTime: 30000
    });

    const candidates = candidatesQuery.data?.candidates ?? [];
    const nextCursor = candidatesQuery.data?.nextCursor ?? null;
    const pageNumber = cursorHistory.length + 1;

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-semibold text-zinc-100">Global Exit Candidates</h1>
                <p className="mt-1 text-sm text-zinc-400">
                    Search immutable exits, inspect provenance and usage, then attach them wherever a workflow needs them.
                </p>
            </div>

            <Card>
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_180px]">
                    <FormSearchInput
                        aria-label="Search global exit candidates"
                        placeholder="Search PT:650, SL:95, #ID, AERO hash, fingerprint, key, or description…"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                    />
                    <FormSelect
                        noMargin
                        aria-label="Candidate source"
                        options={SOURCE_OPTIONS}
                        value={source}
                        onChange={(event) => setSource(event.target.value as CandidateSourceFilter)}
                    />
                    <FormSelect
                        noMargin
                        aria-label="Candidate status"
                        options={[
                            { value: 'active', label: 'Active' },
                            { value: 'invalid', label: 'Invalid' }
                        ]}
                        value={status}
                        onChange={(event) => setStatus(event.target.value === 'invalid' ? 'invalid' : 'active')}
                    />
                </div>
            </Card>

            {candidatesQuery.isLoading ? (
                <DataLoadingSpinner text="Searching global exit candidates…" />
            ) : candidatesQuery.isError ? (
                <EmptyState
                    action={
                        <Button size="sm" variant="outline-dark" onClick={() => candidatesQuery.refetch()}>
                            Retry
                        </Button>
                    }
                    description={candidatesQuery.error.message}
                    title="Could not search exit candidates"
                />
            ) : candidates.length === 0 ? (
                <EmptyState
                    description="Try PT:650, SL:95, #ID, an AERO variant hash, another label, source, or status."
                    title="No global exit candidates match this search"
                />
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                            <thead className="text-left text-xs uppercase tracking-wide text-zinc-500">
                                <tr className="border-b border-zinc-800">
                                    <th className="px-3 py-2 font-medium" scope="col">
                                        Candidate
                                    </th>
                                    <th className="px-3 py-2 font-medium" scope="col">
                                        Source
                                    </th>
                                    <th className="px-3 py-2 font-medium" scope="col">
                                        Exit version
                                    </th>
                                    <th className="px-3 py-2 font-medium" scope="col">
                                        Fingerprint
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map((candidate) => (
                                    <tr className="border-b border-zinc-800/80 hover:bg-zinc-800/40" key={candidate.id}>
                                        <td className="px-3 py-3">
                                            <Link
                                                className="font-medium text-cyan-300 hover:text-cyan-200"
                                                to={`/user/exitCandidate/${candidate.id}`}
                                            >
                                                {candidate.label}
                                            </Link>
                                            <div className="mt-1 font-mono text-xs text-zinc-500">#{candidate.id}</div>
                                        </td>
                                        <td className="px-3 py-3 text-zinc-300">{SOURCE_LABELS[candidate.source]}</td>
                                        <td className="px-3 py-3 text-zinc-300">#{candidate.tradeLogicV2ExitVersionId}</td>
                                        <td className="max-w-72 truncate px-3 py-3 font-mono text-xs text-zinc-500">
                                            {candidate.exitFingerprint ?? candidate.candidateHash}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                        <Button
                            disabled={cursorHistory.length === 0}
                            size="sm"
                            variant="outline-dark"
                            onClick={() => {
                                const previousHistory = cursorHistory.slice(0, -1);
                                setCursor(cursorHistory.at(-1) ?? null);
                                setCursorHistory(previousHistory);
                            }}
                        >
                            Previous
                        </Button>
                        <span className="text-sm text-zinc-500">Page {pageNumber}</span>
                        <Button
                            disabled={nextCursor === null}
                            size="sm"
                            variant="outline-dark"
                            onClick={() => {
                                if (nextCursor === null) return;
                                setCursorHistory((history) => [...history, cursor]);
                                setCursor(nextCursor);
                            }}
                        >
                            Next
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
};

const ExitCandidates = () => {
    const { candidateId: candidateIdParam } = useParams<{ candidateId?: string }>();
    const candidateId = useMemo(() => {
        const parsed = Number(candidateIdParam);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    }, [candidateIdParam]);

    return (
        <UserLayout containerSize="1400px" title="Global Exit Candidates - Project 314">
            {candidateIdParam === undefined ? (
                <ExitCandidateList />
            ) : candidateId === null ? (
                <EmptyState title="Invalid global exit candidate ID" />
            ) : (
                <ExitCandidateDetail candidateId={candidateId} />
            )}
        </UserLayout>
    );
};

export default ExitCandidates;
